/**
 * Zod schemas for API responses. Validate at the fetch boundary so a
 * Lambda → frontend contract drift surfaces as a clear ZodError with
 * field paths, not as a TypeError twenty stack frames deep in render.
 *
 * Motivating bug: the `views` Lambda was returning JSONB as a string;
 * the frontend's TS type said `config: ViewConfig`. No runtime check
 * caught the mismatch — Pipeline.tsx tried to `.filters.map()` on a
 * string and the whole site white-screened.
 *
 * Pattern: define the schema once, derive the TS type with z.infer<>.
 * Hand-written types in `@/types/domain` remain for backwards compat
 * (a separate batch can converge them).
 */

import { z } from 'zod'

// ── Common building blocks ──────────────────────────────────────────────────

const isoDate = z.string()           // We don't bother validating the YYYY-MM-DD shape here;
                                     // the server format isn't always identical (sometimes timestamptz).
const nullableNumber = z.number().nullable().optional()
const nullableString = z.string().nullable().optional()

// ── Lookup tables ───────────────────────────────────────────────────────────

export const businessSchema = z.object({
  id: z.string(),
  slug: z.string(),
  label: z.string(),
  color: z.string(),
  industry: nullableString,
  is_archived: z.boolean(),
  // Per-business RFP checkpoint template (#9LywwTrq). Listed here or z.object()
  // strips it from every businessList consumer (the guided-create prefill bug).
  checkpoint_template: z.array(z.object({ label: z.string(), offset_days: z.number() })).optional(),
})
export type Business = z.infer<typeof businessSchema>

export const verticalSchema = z.object({
  key: z.string(),
  label: z.string(),
  color: z.string(),
})
export type Vertical = z.infer<typeof verticalSchema>

export const stageSchema = z.object({
  id: z.string().optional(),
  key: z.string(),
  label: z.string(),
  position: z.number(),
  color: z.string(),
  entry_criteria: z.string(),
  exit_criteria: z.string(),
  // Gate criteria (#x0mIolVD, 0141/0142). MUST be listed or z.object() strips it
  // and every gate renders as ungated — the same silent-strip class as
  // contact_ids. Optional + default so a response from before the migration, and
  // the mock mirror, both still parse; `useApiGet` throws on a mismatch and
  // stageList guards seven pages.
  criteria: z.array(z.object({
    id: z.string(),
    label: z.string(),
    auto: z.boolean(),
    auto_key: nullableString.optional(),
  })).optional().default([]),
  wip_limit: z.number().nullable().optional(),
  // Staleness settings (#tptb61t4, migration 0145). Optional for the same reason
  // as allows_outcome below — the mock mirror predates the columns — and
  // nullable on the threshold because NULL is the meaningful "never flag" value
  // rather than missing data.
  stale_after_days: z.number().nullable().optional(),
  stale_notify: z.boolean().optional(),
  // Initial business assumptions (#UyUJ65mP, migration 0146), editable by an
  // admin. null means unconfigured; 0 is a configured probability.
  win_probability: z.number().int().min(0).max(100).nullable().optional(),
  // Explicit per-stage close-out flag (0130, #jZdOPrXj). Optional so the mock
  // mirror (mock-api.ts), which predates the column, still parses.
  allows_outcome: z.boolean().optional(),
  // WHICH outcome entering this stage means (0134, #o0Huy9Ur) — drives the
  // server-side stage/outcome sync and the Closed lanes on the board.
  // allows_outcome only says an outcome CAN be recorded here.
  //
  // All THREE dispositions, 'closed' included. This union is not cosmetic:
  // useApiGet THROWS on a schema mismatch, and `stageList` guards seven call
  // sites (Dashboard, Pipeline, DealDetail, DealNew, NegotiationDetail,
  // ActiveNegotiations, Rfps). 0134 seeds `<slug>_closed_no_pursuit` with
  // outcome_on_enter='closed' on every board, so omitting it here would take
  // out the whole pipeline UI the moment the migration ran.
  outcome_on_enter: z
    .union([z.literal('won'), z.literal('lost'), z.literal('closed'), z.null()])
    .optional(),
  business_id: z.string().nullable().optional(),
})
export type Stage = z.infer<typeof stageSchema>

export const referralSourceSchema = z.object({
  id: z.string(),
  key: z.string(),
  label: z.string(),
  category: z.string(),
  sort_order: z.number(),
  is_archived: z.boolean(),
})
export type ReferralSource = z.infer<typeof referralSourceSchema>

export const serviceTypeSchema = z.object({
  id: z.string(),
  key: z.string(),
  label: z.string(),
  description: nullableString,
  sort_order: z.number(),
  is_archived: z.boolean(),
})
export type ServiceType = z.infer<typeof serviceTypeSchema>

export const lossReasonSchema = z.object({
  id: z.string(),
  key: z.string(),
  label: z.string(),
  description: nullableString,
  sort_order: z.number(),
  is_archived: z.boolean(),
})
export type LossReason = z.infer<typeof lossReasonSchema>

// ── Deals + RFPs ────────────────────────────────────────────────────────────

export const dealSchema = z.object({
  id: z.string(),
  name: z.string(),
  value_yr: nullableNumber,
  stage: z.string(),
  // The two axes split out of stage (#o0Huy9Ur, migration 0133). MUST be listed:
  // z.object() STRIPS unlisted keys, so an omitted field reloads blank and the
  // next save writes null over it — the exact bug loss_notes and prep_sheet each
  // hit below. Kept loose (z.string()) rather than a union of the five/three
  // literals so a value added by a future migration renders rather than making
  // the whole deal fail to parse; lib/dealFields.ts owns the vocabulary and the
  // CHECK constraint owns enforcement.
  deal_path: nullableString,
  status: z.string().optional(),
  blocker_reason: nullableString,
  vertical: z.string(),
  outcome: z.union([z.literal('won'), z.literal('lost'), z.literal('closed'), z.null()]).optional(),
  outcome_date: nullableString,
  notes: z.string(),
  position: z.number(),
  owner_id: z.string().nullable().optional(),
  source_channel: z.string(),
  stage_entered_at: z.string(),
  primary_contact_id: z.string().nullable().optional(),
  primary_rfp_id: z.string().nullable().optional(),
  business_id: z.string().nullable().optional(),
  referral_source_id: z.string().nullable().optional(),
  referred_by_contact_id: z.string().nullable().optional(),
  contract_start: nullableString,
  renewal_date: nullableString,
  // No `acv` (2026-09-04) — SELECT_DEAL stopped returning it. Listing a key the
  // server never sends would make every deal fail this parse.
  service_type_id: z.string().nullable().optional(),
  loss_reason_id: z.string().nullable().optional(),
  iteration_id: z.string().nullable().optional(),
  organization_id: z.string().nullable().optional(),
  competitor: nullableString,
  // Loss post-mortem free text (the "why did we lose" box in DealPanel's
  // outcome section + the lost-transition modal). MUST be listed: SELECT_DEAL
  // returns it, and without the key z.object() strips it, so the textarea
  // reloaded empty and re-saving the post-mortem overwrote the stored note
  // with null.
  loss_notes: nullableString,
  // Competitive prep sheet (#dtK4glwb) — jsonb object keyed by section, parsed
  // to a native object by deals_logic._parse_custom_fields. Listed here or
  // z.object() strips it and PrepSheetCard mounts blank on every reload — then
  // its Save (which drops empty sections) writes prep_sheet: null over the
  // stored sheet. Values are typed `unknown`, not `string`, ON PURPOSE: this is
  // a hand-editable jsonb blob, and a strict value type would fail the WHOLE
  // /deals array parse over one stray non-string section. PrepSheetCard already
  // coerces with .toString().
  prep_sheet: z.record(z.string(), z.unknown()).nullable().optional(),
  // Tracker "Next Action" columns, carried through convert (0079). Keep in
  // sync with SELECT_DEAL in deals_logic.py — z.object() strips unknown keys.
  next_action: nullableString,
  next_action_at: nullableString,
  // When the deal should close by (#2r59jCfc, migration 0139). A target — not
  // outcome_date, which is when it actually closed.
  target_close_date: nullableString,
  // The Annual Fee Value predictors (#2r59jCfc, migration 0144). The value
  // itself is `value_yr` above (2026-09-04); `annual_fee_value` is gone from the
  // API, so it is deliberately NOT listed here.
  //
  // Provenance is server-owned. Not an enum on purpose: the server owns the
  // vocabulary and a value it added would fail the WHOLE /deals parse on the
  // client rather than degrade — the same reasoning prep_sheet's `unknown` uses.
  annual_fee_source: nullableString.optional(),
  claims_volume: nullableNumber.optional(),
  current_annual_fee: nullableNumber.optional(),
  // Per-deal follow-up cadence (0096) — listed or z.object() strips it.
  followup_every_days: nullableNumber.optional(),
  archived_at: nullableString,
  custom_fields: z.record(z.string(), z.unknown()).default({}),
  created_at: z.string().optional(),
  // SELECT_DEAL projects this and z.object() was stripping it, so the column
  // never reached the client (#2r59jCfc — found by test_deal_column_parity).
  updated_at: z.string().optional(),
  opened_at: nullableString,
  // Keep these keys in sync with SELECT_DEAL in bd-core/deals_logic.py —
  // z.object() strips unknown keys, so a missing key silently drops the column
  // from every parsed response. `contact_ids` being absent here made DealPanel
  // rebuild the M:N list from just the primary contact and permanently unlink
  // the rest on the next save (2026-07-02 audit P0).
  contact_ids: z.array(z.string()).nullable().optional(),
  priority: nullableString,
  days_in_stage: z.number().nullable().optional(),
  source_detail: nullableString,
  win_loss_reason: nullableString,
  // The prospect this deal was opened from (29ea799). Read-only; the edge is
  // owned by prospects.deal_id. Drives the merged notes trail on DealDetail.
  prospect_id: nullableString,
  // stages.allows_outcome of this deal's CURRENT stage, joined by SELECT_DEAL
  // (0130, #jZdOPrXj). MUST be listed or z.object() strips it — and with it
  // the close-out card on deals stranded on an ARCHIVED late stage, which the
  // archived-filtered /stages picker list cannot vouch for.
  allows_outcome: z.boolean().nullable().optional(),
  // Staleness (#tptb61t4, migration 0145). All four MUST be listed for the
  // reason the comment above contact_ids gives: z.object() strips undeclared
  // keys silently, and a stripped `is_stale` would make every deal render as
  // healthy — a flag that is always off looks like "nothing is stale" rather
  // than like a bug, which is the worst way for this to fail.
  //
  // `stage_visits` is what makes a flagged deal with a short-looking clock
  // explainable; `stale_reason` is which of the two triggers fired.
  stage_visits: z.number().nullable().optional(),
  stale_after_days: z.number().nullable().optional(),
  is_stale: z.boolean().nullable().optional(),
  stale_reason: z
    .union([z.literal('days_in_stage'), z.literal('next_step_overdue'),
            z.literal('both'), z.null()])
    .optional(),
})
export type Deal = z.infer<typeof dealSchema>

/**
 * Structured RFP fee schedule (#NcDOqJ7H) — the San Mateo "proper fee schedule"
 * shape stored inside the `pricing` jsonb. Every field is optional and unknown
 * keys pass through: this is a HAND-EDITABLE jsonb blob, and a strict schema
 * here would fail the entire RFP fetch over one stray field. Real coercion
 * (units, amount padding, defaults) happens in @/lib/feeSchedule.
 */
const feeScheduleSchema = z
  .object({
    title: z.string().optional(),
    intro: z.string().optional(),
    term_years: z.array(z.object({
      label: z.string().optional(),
      period: z.string().optional(),
      renewal: z.boolean().optional(),
    }).passthrough()).optional(),
    options: z.array(z.object({
      name: z.string().optional(),
      description: z.string().optional(),
      groups: z.array(z.object({
        title: z.string().optional(),
        note: z.string().optional(),
        rows: z.array(z.object({
          label: z.string().optional(),
          // The unit basis — the field that makes a fee comparable between
          // bidders, and the thing the old single-number quote lacked.
          unit: z.string().optional(),
          amounts: z.array(z.number().nullable()).optional(),
          tier: z.string().optional(),
          note: z.string().optional(),
        }).passthrough()).optional(),
      }).passthrough()).optional(),
    }).passthrough()).optional(),
    assumptions: z.array(z.object({
      label: z.string().optional(),
      inventory: z.string().optional(),
      volume: z.string().optional(),
    }).passthrough()).optional(),
    assumptions_note: z.string().optional(),
    included: z.array(z.string()).optional(),
    excluded: z.array(z.string()).optional(),
    nte_note: z.string().optional(),
  })
  .passthrough()

export const rfpSchema = z.object({
  id: z.string(),
  org_name: z.string(),
  vertical: z.string(),
  est_value: nullableNumber,
  deadline: isoDate,
  status: z.string(),
  // `owner_id` — NOT `owner`. z.object() strips unknown keys, so a name
  // mismatch here silently drops the column from every parsed response
  // (the 2026-06-09 "Assign to doesn't take" bug). Keep these keys in sync
  // with SELECT_RFP_LIST in bd-core/handler_rfps.py — the LIST projection,
  // which is the narrower of the two and what every schema-validated caller
  // here receives. SELECT_RFP (single-row reads) adds response_draft and
  // prep_sheet on top. response_draft stays deliberately absent: its only
  // reader (RfpResponse.tsx) uses untyped api.get on the detail endpoint.
  // prep_sheet CANNOT be absent — RfpDetail parses the detail endpoint
  // through THIS schema, and stripping it mounted PrepSheetCard blank on
  // every reload, whose Save then wrote prep_sheet: null over the stored
  // sheet. Same bug, same fix, as dealSchema.prep_sheet above.
  owner_id: z.string().nullable().optional(),
  received_date: nullableString,
  deal_id: z.string().nullable().optional(),
  primary_contact_id: z.string().nullable().optional(),
  business_id: z.string().nullable().optional(),
  service_type_id: z.string().nullable().optional(),
  organization_id: z.string().nullable().optional(),
  rfp_number: nullableString,
  submission_method: nullableString,
  submitted_at: nullableString,
  decision_date: nullableString,
  decision_outcome: nullableString,
  decision_reason: nullableString,
  s3_key: nullableString,
  archived_at: nullableString,
  // Async PDF extraction state (migration 0067) — null | pending | running |
  // done | failed. MUST be listed here: z.object() strips unknown keys, and
  // without these the RFP detail page can't tell whether extraction finished,
  // so the response-builder lock (#8TjDXIt3) would never open.
  extraction_status: nullableString,
  extraction_error: nullableString,
  notes: z.string(),
  // jsonb on the RFP — flat annual fee + add-on line items + cited client
  // references (Trello #FN0fEYkn). Loose by design; the shape lives in the
  // RfpPricing type. Must be listed here or z.object() strips it on parse.
  pricing: z
    .object({
      base_annual_fee: nullableNumber,
      cadence: z.string().optional(),
      line_items: z.array(z.object({ label: z.string(), amount: nullableNumber, cadence: z.string().optional() })).optional(),
      // Structured fee schedule in the San Mateo style (#NcDOqJ7H). MUST be
      // listed here or z.object() strips it on parse and the whole rate table
      // vanishes on every reload (the known zod-strip bug class). Kept loose —
      // @/lib/feeSchedule.normalizeFeeSchedule() is what coerces it, so an older
      // or hand-edited row degrades instead of failing the whole RFP parse.
      fee_schedule: feeScheduleSchema.nullable().optional(),
      reference_org_ids: z.array(z.string()).optional(),
      notes: z.string().optional(),
    })
    .nullable()
    .optional(),
  // jsonb on the RFP — captured scoring rubric (#lZFSGI6C). Listed here or
  // z.object() strips it on parse.
  scoring_rubric: z
    .array(z.object({ category: z.string(), max_points: nullableNumber, note: z.string().optional() }))
    .nullable()
    .optional(),
  // jsonb on the RFP — Q&A / addendum tracker (#NUBk9z33). Listed here or
  // z.object() strips it on parse.
  addenda: z
    .array(z.object({
      date: z.string(), kind: z.string(), summary: z.string(),
      // Q&A pairing (#NUBk9z33) — MUST be listed or z.object() silently strips
      // the keys on parse (the known zod-strip bug class).
      question: z.string().nullable().optional(),
      answer: z.string().nullable().optional(),
      answered_date: z.string().nullable().optional(),
    }))
    .nullable()
    .optional(),
  // Client-mandated proposal typeface (#7BRiD81B, migration 0106) — a CSS font
  // stack, NULL = the Penser default. MUST be listed or z.object() strips it and
  // the builder silently falls back to Calibri on every reload.
  font_family: nullableString,
  // Lost-RFP post-mortem (0101) — the RFP half of the 0100/0101 pair. The deal
  // half (dealSchema.loss_notes above) was wired when the columns shipped; this
  // one was not, so every stored post-mortem rendered as an empty textarea and
  // re-saving overwrote it. In _RFP_BASE_COLS, so list callers carry it too.
  loss_notes: nullableString,
  // See the projection note above owner_id: present because RfpDetail parses
  // the DETAIL endpoint through this schema. Absent from the list projection,
  // hence optional. Loose value type for the same reason as dealSchema's.
  prep_sheet: z.record(z.string(), z.unknown()).nullable().optional(),
  // Set only when the row is past the Data API's read cap and the server had
  // to drop the heavy columns to answer (#4wKmaHOQ). MUST be listed or
  // z.object() strips it and RfpDetail cannot tell a stranded draft from an
  // absent one — the same strip bug the owner_id note above records, with a
  // worse outcome: prep_sheet renders blank, this one invites an overwrite.
  draft_unreadable: z.boolean().optional(),
})
export type Rfp = z.infer<typeof rfpSchema>

// ── Prospects — warm/early-funnel opportunities (separate from RFPs) ────────

// Keep these keys in sync with SELECT_PROSPECT in bd-core/handler_prospects.py.
// z.object() strips unknown keys, so a name mismatch silently drops the column
// (the recurring assign-to / owner_id bug class).
export const prospectSchema = z.object({
  id: z.string(),
  org_name: z.string(),
  vertical: nullableString,
  est_value: nullableNumber,
  status: z.string(),
  source_channel: z.string(),
  source_email: nullableString,
  contact_name: nullableString,
  primary_contact_id: z.string().nullable().optional(),
  owner_id: z.string().nullable().optional(),
  business_id: z.string().nullable().optional(),
  next_action: nullableString,
  next_action_at: nullableString,
  notes: z.string(),
  deal_id: z.string().nullable().optional(),
  // Set when the prospect converted down the RFP branch of the funnel
  // (convert-to-rfp) instead of directly to a deal. Mirrors deal_id.
  rfp_id: z.string().nullable().optional(),
  archived_at: nullableString,
  created_at: z.string().optional(),
  // BANT + ICP qualification ratings (#vWap9kuw): { budget, authority, need, timeline, icp } 0–3.
  qualification: z.record(z.string(), z.number()).default({}),
  // Per-touch USD cost rollup (#diuYipQ4). SERVER-DERIVED — there are no such
  // columns on `prospects`; SELECT_PROSPECT computes them from the activity
  // rows. Nullable so no existing Prospect-shaped literal breaks; read them as
  // `?? 0`. touch_cost_count counts touches with a non-NULL cost (an explicit
  // $0 counts, an un-costed touch doesn't), which is what makes avg-per-touch
  // meaningful on TouchCostCard.
  touch_cost_total: nullableNumber,
  touch_cost_count: nullableNumber,
})
export type Prospect = z.infer<typeof prospectSchema>

// ── Saved views — the actual bug that motivated this batch ─────────────────

const filterSchema = z.object({
  field: z.string(),
  op: z.string(),
  value: z.unknown(),
})

const sortSchema = z.object({
  field: z.string(),
  dir: z.union([z.literal('asc'), z.literal('desc')]),
})

export const viewConfigSchema = z.object({
  group_by: z.string().nullable().optional(),
  filters: z.array(filterSchema).default([]),
  q: z.string().optional(),
  sort: z.array(sortSchema).default([]),
  visible_fields: z.array(z.string()).default([]),
  chart_type: z.union([
    z.literal('funnel'), z.literal('bar'), z.literal('pie'), z.literal('box'),
  ]).optional(),
})

// The Lambda historically returned config as a JSON string instead of a parsed
// object. We now accept either and normalize to an object — this is the exact
// pattern that would have caught the white-screen bug at the fetch boundary.
const viewConfigInput = z.preprocess((val) => {
  if (typeof val === 'string') {
    try { return JSON.parse(val) } catch { return {} }
  }
  return val
}, viewConfigSchema)

export const savedViewSchema = z.object({
  id: z.string(),
  user_id: z.string().nullable().optional(),
  name: z.string(),
  view_type: z.union([
    z.literal('board'), z.literal('table'), z.literal('roadmap'), z.literal('chart'),
  ]),
  config: viewConfigInput,
  is_default: z.boolean().optional(),
})
export type SavedView = z.infer<typeof savedViewSchema>

// ── Contacts ────────────────────────────────────────────────────────────────

export const contactSchema = z.object({
  id: z.string(),
  name: z.string(),
  // Backend inserts NULL for both (add-modal sends null, CSV imports may omit) —
  // a non-nullable z.string() here failed the whole-array parse and rendered
  // "Contacts (0)" on every client page (2026-07-02 audit).
  role: z.string().nullable(),
  org: z.string().nullable(),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  linkedin_url: z.string().nullable().optional(),
  organization_id: z.string().nullable().optional(),
  referral_source_id: z.string().nullable().optional(),
  referred_by_contact_id: z.string().nullable().optional(),
  is_decision_maker: z.boolean(),
  is_referral_source: z.boolean(),
  color_scheme: z.string(),
  // NULL on rows created without it; rendered through contactInitials().
  avatar_initials: z.string().nullable().optional(),
  last_touched_at: nullableString,
  // Org relationship (grouping + Clients/Prospects tabs on /contacts).
  // org_name is the FK org's canonical name; the booleans say whether the
  // contact's org (FK, else the free-text name) is a client / has a contract.
  org_name: z.string().nullable().optional(),
  org_is_client: z.boolean().optional(),
  org_has_contract: z.boolean().optional(),
})
export type Contact = z.infer<typeof contactSchema>

// ── Goals + activity (read-side only — write-side mutations stay untyped) ──

export const goalSchema = z.object({
  id: z.string(),
  business_id: z.string().nullable(),
  business_slug: nullableString,
  business_label: nullableString,
  role: nullableString,
  user_id: z.string().nullable(),
  metric: z.string(),
  label: z.string(),
  target_value: z.number(),
  current_value: z.number(),
  period_start: z.string(),
  period_end: z.string(),
  is_archived: z.boolean(),
})
export type Goal = z.infer<typeof goalSchema>

export const activityEventSchema = z.object({
  id: z.string(),
  // Migration 0042 widened the DB CHECK to prospect + client; ClientCrmFeed and
  // EntityActivity post those types. Omitting them here made ONE such row fail
  // the whole-array parse and silently blank the Dashboard feed (2026-07-02 audit).
  entity_type: z.union([
    z.literal('deal'), z.literal('rfp'), z.literal('contact'),
    z.literal('prospect'), z.literal('client'),
  ]),
  entity_id: z.string(),
  icon: z.string(),
  // Some legacy / automation rows have kinds we don't yet enumerate; treat as
  // free-form so a new server-side kind doesn't blow up the boundary parse.
  kind: z.string(),
  description: z.string(),
  // The backend `activity` table column is `actor_label` (display string) and
  // `actor_user_id` (FK). Older Dashboard code read `actor` directly, which
  // was always undefined — keep it optional so the parse stays compatible
  // until the consumers converge on `actor_label`.
  actor: z.string().optional(),
  actor_label: nullableString,
  actor_user_id: nullableString,
  occurred_at: z.string(),
  duration_min: nullableNumber,
  outcome: nullableString,
  next_action: nullableString,
  next_action_at: nullableString,
  // What this touch cost us, USD (#diuYipQ4). Required here (not just on the
  // domain.ts type) because this schema IS parsed at runtime — activityList
  // gates the Dashboard and RfpDetail feeds, and z.object() would strip the key.
  cost_usd: nullableNumber,
})
export type ActivityEvent = z.infer<typeof activityEventSchema>

// ── List-of-X helpers ───────────────────────────────────────────────────────

export const dealList            = z.array(dealSchema)
export const rfpList             = z.array(rfpSchema)
export const prospectList        = z.array(prospectSchema)
export const stageList           = z.array(stageSchema)
export const verticalList        = z.array(verticalSchema)
export const businessList        = z.array(businessSchema)
export const referralSourceList  = z.array(referralSourceSchema)
export const serviceTypeList     = z.array(serviceTypeSchema)
export const lossReasonList      = z.array(lossReasonSchema)
export const contactList         = z.array(contactSchema)
export const savedViewList       = z.array(savedViewSchema)
export const goalList            = z.array(goalSchema)
export const activityList        = z.array(activityEventSchema)
